import { useState, useMemo, useCallback, useEffect } from "react";
import { api, openFile } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/mockups/ui";
import { FileInput } from "@/components/FileInput";
import { toast } from "sonner";
import {
  Plus, FileText, Pencil, Trash2, Copy, Download, ReceiptText, Search,
  ChevronsUpDown, Check, Paperclip, Link2, X, Bell, Mail, AlertTriangle, DownloadCloud, FileUp,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ESTADOS = [
  { key: "estimacion", label: "Estimación" },
  { key: "confirmado_pendiente_po", label: "Confirmado pendiente de PO" },
  { key: "para_facturar", label: "Para facturar" },
  { key: "facturado", label: "Facturado" },
  { key: "confirmado_cobro_amex", label: "Confirmado cobro Amex" },
  { key: "cobrado", label: "Cobrado cliente" },
  { key: "recibido_cobro_cuenta", label: "Recibido cobro en cuenta" },
  { key: "pagada_factura", label: "Pagada factura vinculada" },
];
const ESTADO_LABEL = Object.fromEntries(ESTADOS.map((e) => [e.key, e.label]));
const REQUIERE_PO = new Set(["confirmado_cobro_amex", "cobrado", "recibido_cobro_cuenta", "pagada_factura"]);
const BADGE = {
  estimacion: "bg-slate-100 text-slate-600 border-slate-200",
  confirmado_pendiente_po: "bg-amber-50 text-amber-700 border-amber-200",
  para_facturar: "bg-blue-50 text-blue-700 border-blue-200",
  facturado: "bg-violet-50 text-violet-700 border-violet-200",
  confirmado_cobro_amex: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cobrado: "bg-emerald-50 text-emerald-700 border-emerald-200",
  recibido_cobro_cuenta: "bg-green-50 text-green-700 border-green-200",
  pagada_factura: "bg-teal-50 text-teal-700 border-teal-200",
};

const fmtEur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const mesLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString("es-ES", { month: "short", year: "numeric" });
};

function Combobox({ items, value, onChange, placeholder, testid, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const sel = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid={testid}
          className="w-full justify-between font-normal h-10 border-slate-200">
          <span className={cn("truncate", !sel && "text-muted-foreground")}>{sel ? sel.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Buscar…" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }}>
                  <X className="mr-2 h-4 w-4 opacity-50" /> Sin asignar
                </CommandItem>
              )}
              {items.map((i) => (
                <CommandItem key={i.value} value={i.label} onSelect={() => { onChange(i.value); setOpen(false); }}>
                  <Check className={cn("mr-2 h-4 w-4", value === i.value ? "opacity-100" : "opacity-0")} />
                  {i.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PaymentDialog({ open, onOpenChange, payment, companies, suppliers, projects, onSaved }) {
  const editing = !!payment;
  const [form, setForm] = useState(() => ({
    concepto: payment?.concepto || "",
    cliente_id: payment?.cliente_id || null,
    proveedor_id: payment?.proveedor_id || null,
    project_id: payment?.project_id || null,
    mes: payment?.mes || new Date().toISOString().slice(0, 7),
    importe_proveedor: payment?.importe_proveedor ?? "",
    fecha_prevista_pago: payment?.fecha_prevista_pago || "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const presupuesto = useMemo(() => {
    const b = parseFloat(form.importe_proveedor);
    return isNaN(b) ? 0 : Math.round(b * 1.035 * 100) / 100;
  }, [form.importe_proveedor]);

  const save = async () => {
    if (!form.concepto.trim()) { toast.error("El concepto es obligatorio"); return; }
    const base = parseFloat(form.importe_proveedor);
    if (isNaN(base)) { toast.error("Introduce el importe del proveedor"); return; }
    setSaving(true);
    const body = {
      concepto: form.concepto.trim(),
      cliente_id: form.cliente_id, proveedor_id: form.proveedor_id, project_id: form.project_id,
      mes: form.mes, importe_proveedor: base, fecha_prevista_pago: form.fecha_prevista_pago || null,
    };
    try {
      if (editing) await api.put(`/external-payments/${payment.id}`, body);
      else await api.post("/external-payments", body);
      toast.success(editing ? "Pago actualizado" : "Pago creado");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/external-payments/${payment.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura vinculada subida"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
  };
  const removeInvoice = async () => {
    try { await api.delete(`/external-payments/${payment.id}/invoice`); toast.success("Factura eliminada"); onSaved(); onOpenChange(false); }
    catch { toast.error("Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar pago externo" : "Nuevo pago externo"}</DialogTitle>
          <DialogDescription className="sr-only">Formulario de pago externo</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Concepto *</Label>
            <Input value={form.concepto} onChange={(e) => set("concepto", e.target.value)}
              placeholder="Ej. Impresión de vinilos" data-testid="extpay-concepto" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Combobox items={companies} value={form.cliente_id} onChange={(v) => set("cliente_id", v)}
                placeholder="Empresa…" testid="extpay-cliente" />
            </div>
            <div className="space-y-1.5">
              <Label>Proveedor</Label>
              <Combobox items={suppliers} value={form.proveedor_id} onChange={(v) => set("proveedor_id", v)}
                placeholder="Proveedor…" testid="extpay-proveedor" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Proyecto (opcional)</Label>
            <Combobox items={projects} value={form.project_id} onChange={(v) => set("project_id", v)}
              placeholder="Vincular a un proyecto…" testid="extpay-proyecto" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Mes</Label>
              <Input type="month" value={form.mes} onChange={(e) => set("mes", e.target.value)} data-testid="extpay-mes" />
            </div>
            <div className="space-y-1.5">
              <Label>Importe proveedor (€)</Label>
              <Input type="number" step="0.01" value={form.importe_proveedor}
                onChange={(e) => set("importe_proveedor", e.target.value)} placeholder="0,00" data-testid="extpay-importe" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md bg-primary/5 border border-primary/15 px-3.5 py-2.5">
            <span className="text-sm text-muted-foreground">Presupuesto cliente <span className="text-xs">(+3,5%)</span></span>
            <span className="font-heading font-bold text-primary tabular-nums" data-testid="extpay-presupuesto">{fmtEur(presupuesto)}</span>
          </div>
          <div className="space-y-1.5">
            <Label>Fecha prevista de pago al proveedor</Label>
            <Input type="date" value={form.fecha_prevista_pago} onChange={(e) => set("fecha_prevista_pago", e.target.value)} data-testid="extpay-fecha-prevista" />
            <p className="text-xs text-muted-foreground">Se usará como previsión de pago en Finanzas → Gastos del proyecto.</p>
          </div>

          {editing && (
            <div className="rounded-md border border-slate-200 px-3.5 py-3">
              <div className="flex items-center gap-2 text-sm font-medium mb-2"><Paperclip className="h-4 w-4 text-slate-500" /> Factura vinculada</div>
              {payment.factura_vinculada_name ? (
                <div className="flex items-center justify-between gap-2">
                  <button className="text-sm text-primary underline truncate" onClick={() => openFile(`/external-payments/${payment.id}/invoice`)} data-testid="extpay-invoice-open">
                    {payment.factura_vinculada_name}
                  </button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={removeInvoice} data-testid="extpay-invoice-remove">Quitar</Button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-primary">
                  <input type="file" className="hidden" onChange={uploadInvoice} data-testid="extpay-invoice-input" />
                  <Download className="h-4 w-4 rotate-180" /> Subir factura del proveedor (PDF/imagen)
                </label>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="extpay-save-btn">{saving ? "Guardando…" : (editing ? "Guardar" : "Crear")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TEMPLATE_META = [
  { key: "msg_para_facturar", label: "Para facturar → aviso a Administración" },
  { key: "msg_cobro_amex", label: "Confirmado cobro Amex" },
  { key: "msg_cobro_semanal", label: "Reclamación de cobro (Facturado · inmediato + semanal)" },
  { key: "msg_pago_semanal", label: "Pago al proveedor (Recibido en cuenta · inmediato + semanal)" },
  { key: "msg_factura_externa", label: "Factura vinculada subida" },
  { key: "msg_factura_borrada", label: "Factura vinculada borrada" },
];
const TIPO_LABEL = {
  para_facturar: "Para facturar", cobro_amex: "Cobro Amex", cobro_inmediato: "Cobro (inmediato)",
  cobro_semanal: "Cobro (semanal)", pago_inmediato: "Pago (inmediato)", pago_semanal: "Pago (semanal)",
  factura_externa: "Factura subida", factura_borrada: "Factura borrada",
};

function AvisosDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await api.get("/external-payment-settings");
        setCfg(s.data);
        const e = await api.get("/external-payment-emails");
        setEmails(e.data || []);
      } catch { toast.error("No se pudo cargar la configuración"); }
    })();
  }, [open]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setTpl = (k, v) => setCfg((c) => ({ ...c, templates: { ...c.templates, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/external-payment-settings", {
        admin_email: cfg.admin_email, cc_email: cfg.cc_email, test_mode: cfg.test_mode, templates: cfg.templates,
      });
      toast.success("Configuración de avisos guardada");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="avisos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Avisos por email</DialogTitle>
          <DialogDescription>Emails automáticos del ciclo de pagos externos. Variables: {"{cliente} {proyecto} {concepto} {importe} {numero_factura} {cpc} {po}"}</DialogDescription>
        </DialogHeader>
        {!cfg ? (
          <div className="py-10 text-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" /></div>
        ) : (
          <Tabs defaultValue="config" className="flex-1 overflow-hidden flex flex-col">
            <TabsList>
              <TabsTrigger value="config" data-testid="avisos-tab-config">Configuración</TabsTrigger>
              <TabsTrigger value="historial" data-testid="avisos-tab-historial">Historial ({emails.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="config" className="overflow-y-auto space-y-4 pr-1 mt-3">
              <div className={cn("flex items-start gap-3 rounded-lg border px-4 py-3", cfg.test_mode ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200")}>
                <AlertTriangle className={cn("h-5 w-5 mt-0.5", cfg.test_mode ? "text-amber-600" : "text-emerald-600")} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{cfg.test_mode ? "Modo prueba activado" : "Envío real activado"}</span>
                    <Switch checked={!cfg.test_mode} onCheckedChange={(v) => set("test_mode", !v)} data-testid="avisos-testmode-switch" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {cfg.test_mode
                      ? "Los avisos se REGISTRAN en el historial pero NO se envían. Desactiva el modo prueba para enviar de verdad."
                      : "Los avisos se ENVÍAN por email a los destinatarios de abajo."}
                    {!cfg.resend_configured && " (Resend no está configurado: aunque desactives el modo prueba, no saldrán correos.)"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Destinatario (Administración)</Label><Input value={cfg.admin_email || ""} onChange={(e) => set("admin_email", e.target.value)} data-testid="avisos-admin-email" /></div>
                <div className="space-y-1.5"><Label>Copia (CC)</Label><Input value={cfg.cc_email || ""} onChange={(e) => set("cc_email", e.target.value)} data-testid="avisos-cc-email" /></div>
              </div>
              <div className="space-y-3">
                {TEMPLATE_META.map((t) => (
                  <div key={t.key} className="space-y-1.5">
                    <Label className="text-xs">{t.label}</Label>
                    <Textarea rows={2} value={cfg.templates?.[t.key] || ""} onChange={(e) => setTpl(t.key, e.target.value)} data-testid={`avisos-tpl-${t.key}`} className="text-sm" />
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="historial" className="overflow-y-auto mt-3">
              {emails.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">Aún no hay avisos registrados.</p>
              ) : (
                <div className="space-y-2">
                  {emails.map((e) => (
                    <div key={e.id} className="rounded-md border border-slate-200 px-3 py-2.5" data-testid="avisos-log-item">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{TIPO_LABEL[e.tipo] || e.tipo}</Badge>
                        <span className="text-sm font-medium">{e.subject}</span>
                        <Badge className={cn("text-[10px] ml-auto", e.sent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{e.sent ? "ENVIADO" : "PRUEBA"}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">{e.body}</p>
                      <p className="text-[10.5px] text-slate-400 mt-1">→ {e.to}{e.cc ? ` · CC ${e.cc}` : ""} · {new Date(e.created_at).toLocaleString("es-ES")}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {cfg && <Button onClick={save} disabled={saving} data-testid="avisos-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [tab, setTab] = useState("archivo");
  const [url, setUrl] = useState("https://externo.grabarte360.com/api/integration/export/pagos");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("replace");
  const [fileReport, setFileReport] = useState(null);

  const run = async (dry) => {
    if (!url.trim() || !key.trim()) { toast.error("Introduce la URL y la clave"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/external-payments-import", { export_url: url.trim(), api_key: key.trim(), dry_run: dry });
      setReport({ ...data, dry });
      if (!dry) { toast.success(`Importados ${data.imported} · actualizados ${data.updated}`); onDone && onDone(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error en la importación"); } finally { setBusy(false); }
  };

  const runFile = async (dry) => {
    if (!file) { toast.error("Selecciona el archivo (.xlsx o .zip)"); return; }
    if (!dry && mode === "replace" && !window.confirm("Modo REEMPLAZAR: se borrarán TODOS los pagos externos actuales y se sustituirán por los del archivo. ¿Continuar?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("dry_run", dry ? "true" : "false");
      const { data } = await api.post("/external-payments/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileReport({ ...data, dry });
      if (!dry) { toast.success(`Importados ${data.imported} pagos externos`); onDone && onDone(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo importar el archivo"); } finally { setBusy(false); }
  };

  const close = (o) => { if (!o) { setReport(null); setFileReport(null); setFile(null); } onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DownloadCloud className="h-5 w-5 text-primary" /> Importar pagos externos</DialogTitle>
          <DialogDescription>Sube el archivo exportado del otro Emergent (.xlsx o .zip) o trae los datos desde su enlace.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="archivo" data-testid="import-tab-archivo"><FileUp className="h-4 w-4 mr-1.5" /> Desde archivo</TabsTrigger>
            <TabsTrigger value="enlace" data-testid="import-tab-enlace"><Link2 className="h-4 w-4 mr-1.5" /> Desde enlace</TabsTrigger>
          </TabsList>

          <TabsContent value="archivo" className="space-y-3 py-2">
            <FileInput file={file} onFile={(f) => { setFile(f); setFileReport(null); }} accept=".xlsx,.zip" testid="import-file" />
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Modo:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="import-file-replace" /> Reemplazar todo
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="import-file-merge" /> Actualizar
              </label>
            </div>
            {fileReport && (
              <div className={cn("rounded-md border px-3.5 py-3 text-sm", fileReport.dry ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200")} data-testid="import-file-report">
                <div className="font-semibold mb-1">{fileReport.dry ? "Previsualización" : "Importación completada"}</div>
                <div className="text-xs text-slate-600 space-y-0.5">
                  <div>Pagos en el archivo: <b>{fileReport.total}</b> · con proyecto: <b>{fileReport.with_project}</b> · importe proveedor: <b>{fmtEur(fileReport.total_importe_proveedor)}</b></div>
                  {!fileReport.dry && <div>Importados: <b>{fileReport.imported}</b>{fileReport.mode === "replace" ? " (sustituyeron a los anteriores)" : ""}</div>}
                  {fileReport.unmatched_project_codes?.length > 0 && <div className="text-amber-700">Proyectos sin casar: {fileReport.unmatched_project_codes.join(", ")}</div>}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => runFile(true)} disabled={busy || !file} data-testid="import-file-preview-btn">{busy ? "…" : "Previsualizar"}</Button>
              <Button onClick={() => runFile(false)} disabled={busy || !file} data-testid="import-file-run-btn">{busy ? "Importando…" : "Importar"}</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="enlace" className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>URL del export</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="import-url" /></div>
            <div className="space-y-1.5"><Label>Clave (X-API-Key)</Label><Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Pega aquí la clave" data-testid="import-key" /></div>
            {report && (
              <div className={cn("rounded-md border px-3.5 py-3 text-sm", report.dry ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200")} data-testid="import-report">
                <div className="font-semibold mb-1">{report.dry ? "Previsualización" : "Importación completada"}</div>
                <div className="text-xs text-slate-600 space-y-0.5">
                  <div>Total en origen: <b>{report.total}</b> · con proyecto: <b>{report.with_project}</b> · sin proyecto (bucket): <b>{report.bucket}</b></div>
                  {!report.dry && <div>Nuevos: <b>{report.imported}</b> · actualizados: <b>{report.updated}</b></div>}
                  {report.unmatched_project_codes?.length > 0 && <div className="text-amber-700">Códigos de proyecto sin casar: {report.unmatched_project_codes.join(", ")}</div>}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="import-preview-btn">{busy ? "…" : "Previsualizar"}</Button>
              <Button onClick={() => run(false)} disabled={busy} data-testid="import-run-btn">{busy ? "Importando…" : "Importar"}</Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default function PagosExternos() {
  const { can } = useAuth();
  const canEdit = can("external_payments", "edit");
  const canCreate = can("external_payments", "create");
  const canDelete = can("external_payments", "delete");

  const [fEstado, setFEstado] = useState("todos");
  const [search, setSearch] = useState("");
  const params = fEstado !== "todos" ? { estado: fEstado } : {};
  const { data: rows, loading, refetch } = useFetch("/external-payments", params);
  const { data: summary, refetch: refetchSummary } = useFetch("/external-payments/summary");
  const { data: companiesRaw } = useFetch("/companies");
  const { data: suppliersRaw } = useFetch("/suppliers");
  const { data: projectsRaw } = useFetch("/projects");

  const companies = useMemo(() => (companiesRaw || []).map((c) => ({ value: c.id, label: c.name })), [companiesRaw]);
  const suppliers = useMemo(() => (suppliersRaw || []).map((s) => ({ value: s.id, label: s.name })), [suppliersRaw]);
  const projects = useMemo(() => (projectsRaw || []).map((p) => ({ value: p.id, label: p.reference ? `${p.name} · ${p.reference}` : p.name })), [projectsRaw]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [avisosOpen, setAvisosOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [poDialog, setPoDialog] = useState({ open: false, payment: null, target: "", value: "", cpc: "", numero_factura: "" });

  const reload = useCallback(() => { refetch(); refetchSummary(); }, [refetch, refetchSummary]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) =>
      [r.concepto, r.cliente_name, r.proveedor_name, r.project_name, r.numero_pedido, r.numero_factura]
        .some((v) => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  const doUpdate = async (p, estado, extra = {}) => {
    try {
      await api.put(`/external-payments/${p.id}`, { estado, ...extra });
      toast.success(`Estado: ${ESTADO_LABEL[estado]}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado"); reload(); }
  };

  const changeEstado = (p, estado) => {
    if (estado === p.estado) return;
    const hasPO = !!(p.numero_pedido && String(p.numero_pedido).trim());
    const hasCPC = !!(p.cpc && String(p.cpc).trim());
    const open = () => setPoDialog({ open: true, payment: p, target: estado, value: p.numero_pedido || "", cpc: p.cpc || "", numero_factura: p.numero_factura || "" });
    if (estado === "para_facturar" || estado === "facturado") { open(); return; }
    if (estado === "confirmado_cobro_amex") { if (!hasPO || !hasCPC) { open(); return; } doUpdate(p, estado); return; }
    if (REQUIERE_PO.has(estado) && !hasPO) { open(); return; }
    doUpdate(p, estado);
  };

  const confirmPO = () => {
    const { payment, value, cpc, numero_factura, target } = poDialog;
    const po = (value || "").trim(), cpcv = (cpc || "").trim(), nf = (numero_factura || "").trim();
    if (target === "confirmado_cobro_amex") {
      if (!po) { toast.error("Introduce el número de pedido (PO)."); return; }
      if (!cpcv) { toast.error("Introduce el CPC."); return; }
    } else if (target !== "para_facturar" && target !== "facturado" && REQUIERE_PO.has(target) && !po) {
      toast.error("Introduce un número de pedido (PO)."); return;
    }
    if (target === "facturado" && !nf) { toast.error("Introduce el número de factura."); return; }
    const extra = {};
    if (po) extra.numero_pedido = po;
    if (cpcv) extra.cpc = cpcv;
    if (nf) extra.numero_factura = nf;
    doUpdate(payment, target, extra);
    setPoDialog({ open: false, payment: null, target: "", value: "", cpc: "", numero_factura: "" });
  };

  const del = async () => {
    try { await api.delete(`/external-payments/${toDelete.id}`); toast.success("Pago eliminado"); setToDelete(null); reload(); }
    catch { toast.error("Error al eliminar"); }
  };
  const duplicate = async (p) => {
    try { await api.post(`/external-payments/${p.id}/duplicate`); toast.success("Pago duplicado"); reload(); }
    catch { toast.error("No se pudo duplicar"); }
  };

  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exportPdf = () => {
    const ids = [...selected];
    openFile(`/external-payments/budget.pdf${ids.length ? `?ids=${ids.join(",")}` : ""}`);
  };

  const kpis = summary ? [
    { l: "Conceptos", v: summary.total_conceptos },
    { l: "Importe proveedor", v: fmtEur(summary.total_proveedor) },
    { l: "Presupuestado", v: fmtEur(summary.total_presupuesto), green: true },
    { l: "Margen (3,5%)", v: fmtEur(summary.margen), green: true },
    { l: "Pendientes", v: summary.recordatorios_pendientes, alert: true },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="pagos-externos-page">
      <PageHeader title="Pagos externos" subtitle="Pagos a proveedores gestionados por cuenta de un cliente · margen +3,5%">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportPdf} data-testid="extpay-pdf-btn">
            <FileText className="h-4 w-4" /> Presupuesto PDF{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          {canEdit && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAvisosOpen(true)} data-testid="extpay-avisos-btn">
              <Bell className="h-4 w-4" /> Avisos
            </Button>
          )}
          {canCreate && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="extpay-import-btn">
              <DownloadCloud className="h-4 w-4" /> Importar
            </Button>
          )}
          {canCreate && (
            <Button size="sm" className="gap-1.5" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="extpay-new-btn">
              <Plus className="h-4 w-4" /> Nuevo pago
            </Button>
          )}
        </div>
      </PageHeader>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map((c, i) => (
          <Card key={i} data-testid={`extpay-kpi-${i}`} className={cn("border-border shadow-none p-4", c.alert && c.v > 0 && "border-amber-300 bg-amber-50/40")}>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.l}</div>
            <div className={cn("font-heading text-2xl font-bold mt-1.5 tabular-nums", c.green && "text-primary", c.alert && c.v > 0 && "text-amber-600")}>{c.v}</div>
          </Card>
        ))}
      </div>

      {/* Pipeline strip */}
      {summary && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="extpay-pipeline">
          {ESTADOS.map((e) => (
            <button key={e.key} onClick={() => setFEstado(fEstado === e.key ? "todos" : e.key)}
              data-testid={`extpay-stage-${e.key}`}
              className={cn("rounded-md border px-3 py-2 text-left transition-colors", BADGE[e.key],
                fEstado === e.key ? "ring-2 ring-offset-1 ring-current" : "hover:opacity-80")}>
              <div className="text-[10.5px] font-semibold leading-tight">{e.label}</div>
              <div className="font-heading text-lg font-bold">{summary.por_estado?.[e.key] ?? 0}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={fEstado} onValueChange={setFEstado}>
          <SelectTrigger className="w-56" data-testid="extpay-filter-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar concepto, cliente, proveedor…"
            className="pl-9 w-72" data-testid="extpay-search" />
        </div>
      </div>

      {/* Table */}
      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-slate-50">
                <th className="w-10 px-3 py-3"></th>
                <th className="text-left px-3 py-3">Mes</th>
                <th className="text-left px-3 py-3">Cliente</th>
                <th className="text-left px-3 py-3">Concepto</th>
                <th className="text-left px-3 py-3">Proyecto</th>
                <th className="text-left px-3 py-3">Estado</th>
                <th className="text-left px-3 py-3">PO</th>
                <th className="text-left px-3 py-3">CPC</th>
                <th className="text-right px-3 py-3">Imp. proveedor</th>
                <th className="text-right px-3 py-3">Presupuesto</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="py-14 text-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} className="py-14 text-center text-muted-foreground text-sm">No hay pagos. Crea uno con “Nuevo pago”.</td></tr>
              ) : filtered.map((p) => (
                <tr key={p.id} data-testid={`extpay-row-${p.id}`} className={cn("border-b border-slate-100 hover:bg-slate-50 transition-colors", selected.has(p.id) && "bg-primary/5")}>
                  <td className="px-3 py-2.5"><Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`extpay-select-${p.id}`} /></td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{mesLabel(p.mes)}</td>
                  <td className="px-3 py-2.5 text-slate-700">{p.cliente_name || "—"}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-800">{p.concepto}</div>
                    {p.proveedor_name && <div className="text-xs text-slate-400">{p.proveedor_name}</div>}
                    {p.finance_linked && <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10 text-primary text-[10.5px] font-semibold"><Link2 className="h-3 w-3" /> En finanzas del proyecto</div>}
                  </td>
                  <td className="px-3 py-2.5">{p.project_name ? <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">{p.project_reference || p.project_name}</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <Select value={p.estado} onValueChange={(v) => changeEstado(p, v)} disabled={!canEdit}>
                      <SelectTrigger data-testid={`extpay-estado-${p.id}`} className={cn("h-8 w-auto min-w-[160px] text-xs font-medium rounded-full border px-3", BADGE[p.estado])}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADOS.map((e) => <SelectItem key={e.key} value={e.key} data-testid={`extpay-estado-${p.id}-${e.key}`}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{p.numero_pedido || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600" data-testid={`extpay-cpc-${p.id}`}>{p.cpc || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600 tabular-nums">{fmtEur(p.importe_proveedor)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-primary font-semibold tabular-nums">{fmtEur(p.importe_cliente)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      {p.factura_vinculada_name && (
                        <button title="Factura vinculada" onClick={() => openFile(`/external-payments/${p.id}/invoice`)} data-testid={`extpay-invoice-${p.id}`} className="p-1.5 rounded hover:bg-amber-100 text-amber-600"><ReceiptText className="h-4 w-4" /></button>
                      )}
                      <button title="Presupuesto PDF" onClick={() => openFile(`/external-payments/budget.pdf?ids=${p.id}`)} data-testid={`extpay-pdf-${p.id}`} className="p-1.5 rounded hover:bg-primary/10 text-primary"><Download className="h-4 w-4" /></button>
                      {canCreate && <button title="Duplicar" onClick={() => duplicate(p)} data-testid={`extpay-dup-${p.id}`} className="p-1.5 rounded hover:bg-sky-100 text-sky-600"><Copy className="h-4 w-4" /></button>}
                      {canEdit && <button title="Editar" onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`extpay-edit-${p.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><Pencil className="h-4 w-4" /></button>}
                      {canDelete && <button title="Eliminar" onClick={() => setToDelete(p)} data-testid={`extpay-del-${p.id}`} className="p-1.5 rounded hover:bg-red-100 text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Link2 className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
        <span>Al pasar a <b>Facturado</b> se generan automáticamente el ingreso (+3,5%) y el gasto del proveedor (IVA 21%) en la ficha de Finanzas del proyecto → fluye a Cashflow. Al marcar <b>Cobrado</b>/<b>Pagada vinculada</b> se marcan cobrado/pagado. Los avisos por email llegan en la siguiente fase.</span>
      </div>

      {dialogOpen && (
        <PaymentDialog open={dialogOpen} onOpenChange={setDialogOpen} payment={editing}
          companies={companies} suppliers={suppliers} projects={projects} onSaved={reload} />
      )}

      <AvisosDialog open={avisosOpen} onOpenChange={setAvisosOpen} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={reload} />

      {/* PO / CPC / nº factura dialog */}
      <Dialog open={poDialog.open} onOpenChange={(o) => !o && setPoDialog({ open: false, payment: null, target: "", value: "", cpc: "", numero_factura: "" })}>
        <DialogContent className="sm:max-w-md" data-testid="extpay-po-dialog">
          <DialogHeader>
            <DialogTitle>Pasar a “{ESTADO_LABEL[poDialog.target] || "…"}”</DialogTitle>
            <DialogDescription>
              {poDialog.target === "para_facturar" ? "Introduce el número de pedido (PO) y el CPC. Puedes dejarlos vacíos."
                : poDialog.target === "facturado" ? "Introduce el número de factura (y el PO si lo tienes)."
                : poDialog.target === "confirmado_cobro_amex" ? "Introduce el número de pedido (PO) y el CPC para continuar."
                : "Introduce el número de pedido (PO) para continuar."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-1 space-y-3">
            <div className="space-y-1.5">
              <Label>Nº de pedido (PO)</Label>
              <Input autoFocus value={poDialog.value} onChange={(e) => setPoDialog((d) => ({ ...d, value: e.target.value }))} placeholder="Ej. PO-2026-0045" data-testid="extpay-po-input" />
            </div>
            {(poDialog.target === "para_facturar" || poDialog.target === "confirmado_cobro_amex") && (
              <div className="space-y-1.5">
                <Label>CPC</Label>
                <Input value={poDialog.cpc} onChange={(e) => setPoDialog((d) => ({ ...d, cpc: e.target.value }))} placeholder="Escribe el CPC" data-testid="extpay-cpc-input" />
              </div>
            )}
            {poDialog.target === "facturado" && (
              <div className="space-y-1.5">
                <Label>Nº de factura</Label>
                <Input value={poDialog.numero_factura} onChange={(e) => setPoDialog((d) => ({ ...d, numero_factura: e.target.value }))} placeholder="Ej. 26/0001/000073" data-testid="extpay-nf-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoDialog({ open: false, payment: null, target: "", value: "", cpc: "", numero_factura: "" })}>Cancelar</Button>
            <Button onClick={confirmPO} data-testid="extpay-po-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este pago?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{toDelete?.concepto}” de forma permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="extpay-confirm-del">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
